import React, { useEffect, useMemo, useState } from 'react'
import { Table, Select, message, Space } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import axios from 'axios'

const { Option } = Select

export interface CollToAgencyOrRg2Init {
  collsNameAndRank: { coll_id: string; prename: string; lastname: string; rank: number }[]
  agencies: { agency_id: string; name: string }[]
  rg2AgenciesGroups: { agency_group_id: string; name: string }[]
  collsNegoceMappings: { coll_id: string; negoce: string }[]
}

interface CollRow {
  key: string
  coll_id: string
  prename: string
  lastname: string
  fullName: string
}

interface CollToAgencyOrRg2Props {
  orgaId: string
  initData: CollToAgencyOrRg2Init
}

function uniqueFilterOptions(values: (string | null | undefined)[]) {
  return Array.from(new Set(values.filter((v): v is string => !!v)))
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ text: value, value }))
}

function toCollRows(
  colls: CollToAgencyOrRg2Init['collsNameAndRank'],
  rankPredicate: (rank: number) => boolean,
): CollRow[] {
  return colls
    .filter((coll) => rankPredicate(coll.rank))
    .map((coll) => ({
      key: coll.coll_id,
      coll_id: coll.coll_id,
      prename: coll.prename,
      lastname: coll.lastname,
      fullName: `${coll.prename} ${coll.lastname}`.trim(),
    }))
    .sort((a, b) => a.prename.localeCompare(b.prename) || a.lastname.localeCompare(b.lastname))
}

export function CollToAgencyOrRg2({ orgaId, initData }: CollToAgencyOrRg2Props) {
  const [collNegoceAccesses, setCollNegoceAccesses] = useState<{ coll_id: string; negoce: string }[]>(
    initData.collsNegoceMappings,
  )

  useEffect(() => {
    setCollNegoceAccesses(initData.collsNegoceMappings)
  }, [initData.collsNegoceMappings])

  const agencyNameById = useMemo(() => {
    const map = new Map<string, string>()
    initData.agencies.forEach((a) => map.set(a.agency_id.toLowerCase(), a.name))
    return map
  }, [initData.agencies])

  const getNegocesForColl = (collId: string) =>
    collNegoceAccesses.filter((item) => item.coll_id === collId).map((item) => item.negoce)

  const getAgencyLabelsForColl = (collId: string) =>
    getNegocesForColl(collId)
      .map((id) => agencyNameById.get(id.toLowerCase()) || id)
      .filter(Boolean)

  const handleSelectAgencyOrRg2Change = async (collId: string, negoce: string) => {
    try {
      await axios.post(
        `/organisations/${orgaId}/datacnegoce/colltoagency?coll_id=${collId}&negoce=${encodeURIComponent(negoce)}`,
      )
      setCollNegoceAccesses((prevData) => {
        const withoutColl = prevData.filter((item) => item.coll_id !== collId)
        return [...withoutColl, { coll_id: collId, negoce }]
      })
      message.success('Enregistré')
    } catch (error) {
      console.error('Error when updating negoce:', error)
      message.error("Erreur lors de l'enregistrement")
    }
  }

  const handleSelectAgenciesMultiChange = async (collId: string, negoces: string[]) => {
    const previous = getNegocesForColl(collId)
    try {
      await axios.post(`/organisations/${orgaId}/datacnegoce/colltoagency/multi?coll_id=${collId}`, {
        negoces,
      })
      setCollNegoceAccesses((prevData) => {
        const withoutColl = prevData.filter((item) => item.coll_id !== collId)
        return [...withoutColl, ...negoces.map((negoce) => ({ coll_id: collId, negoce }))]
      })
      message.success('Enregistré')
    } catch (error) {
      console.error('Error when updating negoces:', error)
      message.error("Erreur lors de l'enregistrement")
      setCollNegoceAccesses((prevData) => {
        const withoutColl = prevData.filter((item) => item.coll_id !== collId)
        return [...withoutColl, ...previous.map((negoce) => ({ coll_id: collId, negoce }))]
      })
    }
  }

  const dataSourceRg4 = useMemo(
    () => toCollRows(initData.collsNameAndRank, (rank) => rank === 4),
    [initData.collsNameAndRank],
  )
  const dataSourceRg3 = useMemo(
    () => toCollRows(initData.collsNameAndRank, (rank) => rank === 3),
    [initData.collsNameAndRank],
  )
  const dataSourceRg2Or1 = useMemo(
    () => toCollRows(initData.collsNameAndRank, (rank) => rank === 2 || rank === 1),
    [initData.collsNameAndRank],
  )

  const agencyFilters = useMemo(
    () => uniqueFilterOptions(initData.agencies.map((a) => a.name)),
    [initData.agencies],
  )
  const rg2Filters = useMemo(
    () => uniqueFilterOptions(initData.rg2AgenciesGroups.map((g) => g.name)),
    [initData.rg2AgenciesGroups],
  )

  const columnsRg4: ColumnsType<CollRow> = useMemo(
    () => [
      {
        title: <span style={{ fontWeight: 'bold' }}>Rang 4</span>,
        dataIndex: 'fullName',
        key: 'collaborator',
        filterSearch: true,
        filters: uniqueFilterOptions(dataSourceRg4.map((c) => c.fullName)),
        onFilter: (value, record) => record.fullName === value,
        sorter: (a, b) => a.prename.localeCompare(b.prename) || a.lastname.localeCompare(b.lastname),
        render: (_: unknown, coll) => (
          <span>
            {coll.prename} {coll.lastname}
          </span>
        ),
      },
      {
        title: <span style={{ fontWeight: 'bold' }}>Agences</span>,
        dataIndex: 'agency',
        key: 'agency',
        filterSearch: true,
        filters: [
          { text: 'Non assigné', value: '__unassigned__' },
          ...agencyFilters,
        ],
        onFilter: (value, record) => {
          const labels = getAgencyLabelsForColl(record.coll_id)
          if (value === '__unassigned__') return labels.length === 0
          return labels.includes(String(value))
        },
        render: (_: unknown, coll) => {
          const selected = getNegocesForColl(coll.coll_id)
          return (
            <Select
              mode="multiple"
              value={selected}
              onChange={(values: string[]) => handleSelectAgenciesMultiChange(coll.coll_id, values)}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
              allowClear
              placeholder="Sélectionner des agences"
            >
              {initData.agencies.map((agency) => (
                <Option key={agency.agency_id} value={agency.agency_id}>
                  {agency.name}
                </Option>
              ))}
            </Select>
          )
        },
      },
    ],
    [dataSourceRg4, agencyFilters, collNegoceAccesses, initData.agencies],
  )

  const columnsRg3: ColumnsType<CollRow> = useMemo(
    () => [
      {
        title: <span style={{ fontWeight: 'bold' }}>Rang 3</span>,
        dataIndex: 'fullName',
        key: 'collaborator',
        filterSearch: true,
        filters: uniqueFilterOptions(dataSourceRg3.map((c) => c.fullName)),
        onFilter: (value, record) => record.fullName === value,
        sorter: (a, b) => a.prename.localeCompare(b.prename) || a.lastname.localeCompare(b.lastname),
        render: (_: unknown, coll) => (
          <span>
            {coll.prename} {coll.lastname}
          </span>
        ),
      },
      {
        title: <span style={{ fontWeight: 'bold' }}>Agence</span>,
        dataIndex: 'agency',
        key: 'agency',
        filterSearch: true,
        filters: [
          { text: 'Non assigné', value: '__unassigned__' },
          ...agencyFilters,
        ],
        onFilter: (value, record) => {
          const labels = getAgencyLabelsForColl(record.coll_id)
          if (value === '__unassigned__') return labels.length === 0
          return labels.includes(String(value))
        },
        render: (_: unknown, coll) => {
          const negoceValue =
            collNegoceAccesses.find((item) => item.coll_id === coll.coll_id)?.negoce.toLowerCase() || ''
          const isValidOption = initData.agencies.some(
            (agency) => agency.agency_id.toLowerCase() === negoceValue,
          )
          return (
            <Select
              value={isValidOption ? negoceValue : ''}
              onChange={(value) => handleSelectAgencyOrRg2Change(coll.coll_id, value)}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
              placeholder="Sélectionner une agence"
            >
              {initData.agencies.map((agency) => (
                <Option key={agency.agency_id} value={agency.agency_id}>
                  {agency.name}
                </Option>
              ))}
            </Select>
          )
        },
      },
    ],
    [dataSourceRg3, agencyFilters, collNegoceAccesses, initData.agencies],
  )

  const columnsRg2Or1: ColumnsType<CollRow> = useMemo(
    () => [
      {
        title: <span style={{ fontWeight: 'bold' }}>Rang 2</span>,
        dataIndex: 'fullName',
        key: 'collaborator',
        filterSearch: true,
        filters: uniqueFilterOptions(dataSourceRg2Or1.map((c) => c.fullName)),
        onFilter: (value, record) => record.fullName === value,
        sorter: (a, b) => a.prename.localeCompare(b.prename) || a.lastname.localeCompare(b.lastname),
        render: (_: unknown, coll) => (
          <span>
            {coll.prename} {coll.lastname}
          </span>
        ),
      },
      {
        title: <span style={{ fontWeight: 'bold' }}>Nom du groupement d'agences</span>,
        dataIndex: 'rg2',
        key: 'rg2',
        filterSearch: true,
        filters: [
          { text: 'Non assigné', value: '__unassigned__' },
          ...rg2Filters,
        ],
        onFilter: (value, record) => {
          const negoce =
            collNegoceAccesses.find((item) => item.coll_id === record.coll_id)?.negoce.toLowerCase() ||
            ''
          if (value === '__unassigned__') {
            return (
              !negoce ||
              !initData.rg2AgenciesGroups.some((rg2) => rg2.name.toLowerCase() === negoce)
            )
          }
          return negoce === String(value).toLowerCase()
        },
        render: (_: unknown, coll) => {
          const negoceValue =
            collNegoceAccesses.find((item) => item.coll_id === coll.coll_id)?.negoce.toLowerCase() || ''
          const isValidOption = initData.rg2AgenciesGroups.some(
            (rg2) => rg2.name.toLowerCase() === negoceValue,
          )
          return (
            <Select
              value={isValidOption ? negoceValue : ''}
              onChange={(value) => handleSelectAgencyOrRg2Change(coll.coll_id, value)}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
              placeholder="Sélectionner un groupement d'agences"
            >
              {initData.rg2AgenciesGroups.map((rg2) => (
                <Option key={rg2.agency_group_id} value={rg2.name}>
                  {rg2.name}
                </Option>
              ))}
            </Select>
          )
        },
      },
    ],
    [dataSourceRg2Or1, rg2Filters, collNegoceAccesses, initData.rg2AgenciesGroups],
  )

  return (
    <div>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Table
          columns={columnsRg4}
          dataSource={dataSourceRg4}
          bordered
          pagination={false}
        />
        <Table
          columns={columnsRg3}
          dataSource={dataSourceRg3}
          bordered
          pagination={false}
        />
        <Table
          columns={columnsRg2Or1}
          dataSource={dataSourceRg2Or1}
          bordered
          pagination={false}
        />
      </Space>
    </div>
  )
}
